import type { ReactNode } from 'react'
import { AlertCircle, CheckCircle2, Loader2 } from 'lucide-react'

export function Spinner({ className = 'h-4 w-4' }: { className?: string }) {
  return <Loader2 className={`animate-spin ${className}`} />
}

export function Notice({ kind = 'error', children }: { kind?: 'error' | 'ok' | 'warn'; children: ReactNode }) {
  const tone = { error: 'border-danger/40 bg-danger/10 text-danger', ok: 'border-ok/40 bg-ok/10 text-ok', warn: 'border-warn/40 bg-warn/10 text-warn' }[kind]
  const Icon = kind === 'ok' ? CheckCircle2 : AlertCircle
  return (
    <div className={`flex items-start gap-2 whitespace-pre-line rounded-lg border px-3 py-2 text-sm ${tone}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div>{children}</div>
    </div>
  )
}

export function PageHeader({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
      <h1 className="text-xl font-bold">{title}</h1>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  )
}

const STATUS_TONE: Record<string, string> = {
  published: 'border-ok/40 text-ok',
  draft: 'border-warn/40 text-warn',
  archived: 'text-faint',
  success: 'border-ok/40 text-ok',
  queued: 'border-warn/40 text-warn',
  building: 'border-accent/40 text-accent',
  failed: 'border-danger/40 text-danger',
  dispatch_failed: 'border-danger/40 text-danger',
}

export function StatusChip({ status }: { status: string }) {
  return <span className={`chip ${STATUS_TONE[status] ?? ''}`}>{status.replace('_', ' ')}</span>
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="card border-dashed p-10 text-center text-sm text-muted">{children}</div>
}

/** Relative time for lists: "3 min ago", "2 days ago". */
export function ago(iso: string | null | undefined): string {
  if (!iso) return '—'
  const s = (Date.now() - Date.parse(iso)) / 1000
  if (s < 60) return 'just now'
  const units: [number, string][] = [[60, 'min'], [3600, 'hour'], [86400, 'day'], [2592000, 'month']]
  let label = ''
  for (const [size, name] of units) if (s >= size) label = `${Math.floor(s / size)} ${name}`
  const n = parseInt(label, 10)
  return `${label}${n === 1 ? '' : 's'} ago`
}
